import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import GoogleAudienceForm from '../../../../../AudienceForm'

import { formatOptionsList } from '../../../../../../../features/formatters'

import { clearAudiences, getAudiences } from '../../../../../../../modules/actions/audiences'
import { createAssetGroupSignal, clearCreateAssetGroupSignal } from '../../../../../../../modules/actions/assets'
import { audiencesLoadingSelector, audiencesSelector } from '../../../../../../../modules/selectors/audiences'
import { selectedGoogleAdAccountSelector } from '../../../../../../../modules/selectors/adAccounts'
import {
  createAssetGroupSignalErrorSelector,
  createAssetGroupSignalIsLoadingSelector,
  createAssetGroupSignalWasCreatedSelector
} from '../../../../../../../modules/selectors/assets'

import { AUDIENCE } from './fields'
import { GOOGLE_PLATFORM } from '../../../../../../../constants/selectLists/platformList'
import { IS_QUARANTINE } from '../../../../../../reusableFields'

const GoogleAssetGroupAudienceForm = ({
  assetGroupCreatedData,
  onSuccessSubmit,
  isInternalAssetGroupCreation,
  formProgressItems
}) => {
  const dispatch = useDispatch()

  const audiences = useSelector(audiencesSelector)
  const audiencesIsLoading = useSelector(audiencesLoadingSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const assetGroupSignalWasCreated = useSelector(createAssetGroupSignalWasCreatedSelector)

  const formattedAudiences = useMemo(
    () =>
      formatOptionsList({
        list: audiences,
        labelName: 'name',
        valueName: 'resource_name'
      }),
    [audiences]
  )

  const onSubmit = useCallback(
    values => {
      const { resource_name: assetGroupResourceName, id: assetGroupInternalId } = assetGroupCreatedData

      dispatch(
        createAssetGroupSignal({
          account: selectedAdAccountId,
          [AUDIENCE]: {
            [AUDIENCE]: values[AUDIENCE]
          },
          // audiences signal can be also created internally for internal asset group
          // isInternalAssetGroupCreation is default, also allow to pass it manually if user has ability to select it(values[IS_QUARANTINE])
          ...(isInternalAssetGroupCreation || values[IS_QUARANTINE]
            ? {
                asset_group: assetGroupInternalId,
                quarantine: true
              }
            : {
                asset_group: assetGroupResourceName
              })
        })
      )
    },
    [dispatch, assetGroupCreatedData, selectedAdAccountId, isInternalAssetGroupCreation]
  )

  const clearCreateAssetGroupSignalHandler = useCallback(() => {
    dispatch(clearCreateAssetGroupSignal())
  }, [dispatch])

  useEffect(() => {
    dispatch(
      getAudiences(
        {
          account: selectedAdAccountId,
          limit: 9999
        },
        GOOGLE_PLATFORM
      )
    )

    return () => {
      dispatch(clearAudiences())
    }
  }, [dispatch, selectedAdAccountId])

  return (
    <GoogleAudienceForm
      formName="asset_group_audiences"
      audiencesList={formattedAudiences}
      audiencesIsLoading={audiencesIsLoading}
      audienceWasCreated={assetGroupSignalWasCreated}
      clearHandler={clearCreateAssetGroupSignalHandler}
      errorSelector={createAssetGroupSignalErrorSelector}
      isLoadingSelector={createAssetGroupSignalIsLoadingSelector}
      onSuccessSubmit={onSuccessSubmit}
      formProgressItems={formProgressItems}
      isInternalAssetGroupCreation={isInternalAssetGroupCreation}
      handleSubmit={onSubmit}
    />
  )
}

export default GoogleAssetGroupAudienceForm
